import {
  Box, Button, Card, CardContent, CardHeader, CircularProgress, Divider,
  TextField, MenuItem,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Typography, Paper
} from '@material-ui/core';
import CreditCardIcon from '@material-ui/icons/CreditCard';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { makeStyles } from '@material-ui/core/styles';
import { paymentActions, paymentThunks } from 'src/store/slices/paymentSlice';
import { loadBaslandiJob } from 'src/utils/storage/StartWorkOrder';
import { getUser, moneyFormat } from 'src/utils/Common';

const useStyles = makeStyles((theme) => ({
  root: {
    padding: 0
  },
  content: {
    minHeight: 160,
    padding: '16px'
  },
  table: {
    marginTop: theme.spacing(2),
    marginBottom: theme.spacing(2),
  },
  summaryRow: {
    fontWeight: 700,
  },
  sectionTitle: {
    fontWeight: 600,
    marginTop: theme.spacing(2),
    marginBottom: theme.spacing(1),
  },
  totalBox: {
    marginTop: theme.spacing(2),
    padding: theme.spacing(2),
    borderRadius: 8,
    background: '#f5f7fa',
    border: '1px solid #d8e2ec',
  },
}));

const Payment = ({ ReceiptNo, readonly }) => {
  const classes = useStyles();
  const { t } = useTranslation('work_order');
  const dispatch = useDispatch();
  const SessionUser = getUser() || {};

  // Redux state
  const tasks = useSelector(s => s.tasks1) || {};
  const repairData = useSelector(s => s.payment?.repairPreSaleData) || null;
  const repairLoading = useSelector(s => s.payment?.repairPreSaleLoading) === 'Loading';
  const repairError = useSelector(s => s.payment?.repairPreSaleError) || null;
  const posMatchingList = useSelector(s => s.payment?.employeePosMatching) || [];
  const posMatchingLoading = useSelector(s => s.payment?.employeePosMatchingLoading) === 'Loading';

  // Local state
  const [showIframe, setShowIframe] = useState(false);
  const [localError, setLocalError] = useState('');
  const [selectedPos, setSelectedPos] = useState('');

  // Fetch POS machines on mount
  const technicianId = SessionUser?.TechnicianId;
  useEffect(() => {
    const employeeId = parseInt(technicianId) || 0;
    if (employeeId > 0) {
      dispatch(paymentThunks.GetEmployeePosMatching(employeeId));
    }
    return () => {
      dispatch(paymentActions.clearEmployeePosMatching());
    };
  }, [dispatch, technicianId]);

  // Get current work order data (same pattern as FinalProcess.js)
  const taskData = readonly
    ? (tasks?.tasks || []).filter((d) => parseInt(d.ReceiptNo) === parseInt(ReceiptNo))
    : loadBaslandiJob();

  const workOrder = taskData && taskData[0] ? taskData[0] : null;

  // Calculate costs from work order
  const labours = workOrder?.WorkOrderLabours || [];
  const materials = workOrder?.WorkOrderMaterials || [];
  const transports = workOrder?.WorkOrderTransport || [];
  const extras = workOrder?.WorkOrderExtra || [];

  let labourSum = 0;
  let labourVatSum = 0;
  labours.forEach((row) => {
    labourSum += row.PriceWithWat || 0;
    labourVatSum += row.Price || 0;
  });

  let materialSum = 0;
  let materialVatSum = 0;
  if (workOrder?.WarrantyTypeId !== 471) {
    materials.forEach((row) => {
      materialSum += (row.PriceWithWat || 0) * (row.Count || 1);
      materialVatSum += (row.Price || 0) * (row.Count || 1);
    });
  }

  let transportSum = 0;
  let transportVatSum = 0;
  transports.forEach((row) => {
    transportSum += row.MaterialPriceWithWat || 0;
    transportVatSum += row.MaterialPrice || 0;
  });

  let extraSum = 0;
  let extraVatSum = 0;
  extras.forEach((row) => {
    extraSum += row.MaterialPriceWithWat || 0;
    extraVatSum += row.MaterialPrice || 0;
  });

  const totalWithVat = labourSum + materialSum + transportSum + extraSum;
  const totalWithoutVat = labourVatSum + materialVatSum + transportVatSum + extraVatSum;
  const vatAmount = totalWithVat - totalWithoutVat;

  const fmt = (val) => moneyFormat(val, SessionUser.TenantCode, SessionUser.CurrencyUnit);
  const cur = SessionUser.CurrencyUnit || '';

  const handleRepairPreSale = () => {
    setLocalError('');
    setShowIframe(false);
    dispatch(paymentActions.clearRepairPreSale());

    const selectedPosDevice = posMatchingList.find(p => (p.Id || p.PosId) === selectedPos);
    const posUserRef = selectedPosDevice?.PosMachineIdentifier || String(selectedPos) || '';

    const dto = {
      ApplicationReference: String(ReceiptNo || ''),
      Amount: totalWithVat,
      Currency: cur.toUpperCase() || 'RON',
      UserRef: posUserRef,
      Products: [],
      AdditionalMetaData: '',
      LanguageCode: '',
      CreateEmployeeId: null,
      ConsumerId: null
    };

    dispatch(paymentThunks.RepairPreSale(dto))
      .then(resp => {
        const payload = resp?.payload;
        if (resp?.error) {
          setLocalError(resp.error.message || 'RepairPreSale failed.');
          return;
        }
        if (payload?.PaymentUrl) {
          setShowIframe(true);
        }
      })
      .catch(err => {
        setLocalError(err?.message || 'RepairPreSale exception.');
      });
  };

  const handleCloseIframe = () => {
    setShowIframe(false);
    dispatch(paymentActions.clearRepairPreSale());
  };

  const iframeUrl = repairData?.PaymentUrl;
  const hasAnyCost = labours.length > 0 || materials.length > 0 || transports.length > 0 || extras.length > 0;

  return (
    <Card className={classes.root}>
      <CardHeader title={t('Payment')} />
      <Divider />
      <CardContent className={classes.content}>
        <Typography variant="body1">
          {t('Payment')} - Work Order: <strong>{ReceiptNo}</strong>
        </Typography>

        {/* Labour Table */}
        {labours.length > 0 && (
          <>
            <Typography variant="subtitle1" className={classes.sectionTitle}>
              {t('iscilik_Toplami')}
            </Typography>
            <TableContainer component={Paper} variant="outlined" className={classes.table}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>{t('Aciklama')}</TableCell>
                    <TableCell align="right">{t('Fiyat')} ({cur})</TableCell>
                    <TableCell align="right">{t('Fiyat')} + VAT ({cur})</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {labours.map((row, i) => (
                    <TableRow key={i}>
                      <TableCell>{row.LabourDesc || row.LabourCode || '-'}</TableCell>
                      <TableCell align="right">{fmt(row.Price || 0)}</TableCell>
                      <TableCell align="right">{fmt(row.PriceWithWat || 0)}</TableCell>
                    </TableRow>
                  ))}
                  <TableRow>
                    <TableCell className={classes.summaryRow}>{t('Toplam')}</TableCell>
                    <TableCell align="right" className={classes.summaryRow}>{fmt(labourVatSum)}</TableCell>
                    <TableCell align="right" className={classes.summaryRow}>{fmt(labourSum)}</TableCell>
                  </TableRow>
                </TableBody>
              </Table>
            </TableContainer>
          </>
        )}

        {/* Material Table */}
        {materials.length > 0 && (
          <>
            <Typography variant="subtitle1" className={classes.sectionTitle}>
              {t('malzeme_Toplami')}
            </Typography>
            <TableContainer component={Paper} variant="outlined" className={classes.table}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>{t('Aciklama')}</TableCell>
                    <TableCell align="center">{t('Adet')}</TableCell>
                    <TableCell align="right">{t('Fiyat')} ({cur})</TableCell>
                    <TableCell align="right">{t('Fiyat')} + VAT ({cur})</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {materials.map((row, i) => (
                    <TableRow key={i}>
                      <TableCell>{row.MaterialDesc || row.MaterialCode || '-'}</TableCell>
                      <TableCell align="center">{row.Count || 1}</TableCell>
                      <TableCell align="right">{fmt((row.Price || 0) * (row.Count || 1))}</TableCell>
                      <TableCell align="right">{fmt((row.PriceWithWat || 0) * (row.Count || 1))}</TableCell>
                    </TableRow>
                  ))}
                  <TableRow>
                    <TableCell className={classes.summaryRow} colSpan={2}>{t('Toplam')}</TableCell>
                    <TableCell align="right" className={classes.summaryRow}>{fmt(materialVatSum)}</TableCell>
                    <TableCell align="right" className={classes.summaryRow}>{fmt(materialSum)}</TableCell>
                  </TableRow>
                </TableBody>
              </Table>
            </TableContainer>
          </>
        )}

        {/* Transport Table */}
        {transports.length > 0 && (
          <>
            <Typography variant="subtitle1" className={classes.sectionTitle}>
              {t('yol_Toplami')}
            </Typography>
            <TableContainer component={Paper} variant="outlined" className={classes.table}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>#</TableCell>
                    <TableCell align="right">{t('Fiyat')} ({cur})</TableCell>
                    <TableCell align="right">{t('Fiyat')} + VAT ({cur})</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {transports.map((row, i) => (
                    <TableRow key={i}>
                      <TableCell>{i + 1}</TableCell>
                      <TableCell align="right">{fmt(row.MaterialPrice || 0)}</TableCell>
                      <TableCell align="right">{fmt(row.MaterialPriceWithWat || 0)}</TableCell>
                    </TableRow>
                  ))}
                  <TableRow>
                    <TableCell className={classes.summaryRow}>{t('Toplam')}</TableCell>
                    <TableCell align="right" className={classes.summaryRow}>{fmt(transportVatSum)}</TableCell>
                    <TableCell align="right" className={classes.summaryRow}>{fmt(transportSum)}</TableCell>
                  </TableRow>
                </TableBody>
              </Table>
            </TableContainer>
          </>
        )}

        {/* Extra Costs Table */}
        {extras.length > 0 && (
          <>
            <Typography variant="subtitle1" className={classes.sectionTitle}>
              {t('lblExtraPrice')}
            </Typography>
            <TableContainer component={Paper} variant="outlined" className={classes.table}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>#</TableCell>
                    <TableCell align="right">{t('Fiyat')} ({cur})</TableCell>
                    <TableCell align="right">{t('Fiyat')} + VAT ({cur})</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {extras.map((row, i) => (
                    <TableRow key={i}>
                      <TableCell>{i + 1}</TableCell>
                      <TableCell align="right">{fmt(row.MaterialPrice || 0)}</TableCell>
                      <TableCell align="right">{fmt(row.MaterialPriceWithWat || 0)}</TableCell>
                    </TableRow>
                  ))}
                  <TableRow>
                    <TableCell className={classes.summaryRow}>{t('Toplam')}</TableCell>
                    <TableCell align="right" className={classes.summaryRow}>{fmt(extraVatSum)}</TableCell>
                    <TableCell align="right" className={classes.summaryRow}>{fmt(extraSum)}</TableCell>
                  </TableRow>
                </TableBody>
              </Table>
            </TableContainer>
          </>
        )}

        {/* Grand Total Box */}
        {hasAnyCost && (
          <Box className={classes.totalBox}>
            <Box display="flex" justifyContent="space-between" mb={0.5}>
              <Typography variant="body2" color="textSecondary">{t('iscilik_Toplami')}</Typography>
              <Typography variant="body2"><strong>{fmt(labourSum)} {cur}</strong></Typography>
            </Box>
            <Box display="flex" justifyContent="space-between" mb={0.5}>
              <Typography variant="body2" color="textSecondary">{t('malzeme_Toplami')}</Typography>
              <Typography variant="body2"><strong>{fmt(materialSum)} {cur}</strong></Typography>
            </Box>
            <Box display="flex" justifyContent="space-between" mb={0.5}>
              <Typography variant="body2" color="textSecondary">{t('yol_Toplami')}</Typography>
              <Typography variant="body2"><strong>{fmt(transportSum)} {cur}</strong></Typography>
            </Box>
            {extras.length > 0 && (
              <Box display="flex" justifyContent="space-between" mb={0.5}>
                <Typography variant="body2" color="textSecondary">{t('lblExtraPrice')}</Typography>
                <Typography variant="body2"><strong>{fmt(extraSum)} {cur}</strong></Typography>
              </Box>
            )}
            <Divider style={{ margin: '8px 0' }} />
            <Box display="flex" justifyContent="space-between" mb={0.5}>
              <Typography variant="body2" color="textSecondary">{t('lblVat')}</Typography>
              <Typography variant="body2" style={{ color: '#d32f2f' }}><strong>{fmt(vatAmount)} {cur}</strong></Typography>
            </Box>
            <Box display="flex" justifyContent="space-between">
              <Typography variant="subtitle1" style={{ fontWeight: 700, color: '#d32f2f' }}>{t('Toplam')}</Typography>
              <Typography variant="subtitle1" style={{ fontWeight: 700, color: '#d32f2f' }}>{fmt(totalWithVat)} {cur}</Typography>
            </Box>
          </Box>
        )}

        {/* No cost info */}
        {!hasAnyCost && (
          <Box mt={2}>
            <Typography variant="body2" color="textSecondary">
              No labour, material, or transport costs found for this work order.
            </Typography>
          </Box>
        )}

        {/* POS Machine Selector */}
        {!readonly && (
          <Box
            mt={3}
            style={{
              position: 'relative',
              padding: '16px 14px 14px',
              borderRadius: 20,
              background: 'linear-gradient(145deg,#ffffff,#fcf9f5)',
              border: '1px solid #e2d5d5',
              boxShadow: '0 2px 8px -1px rgba(70,40,15,.08)',
            }}
          >
            <Typography
              variant="subtitle2"
              style={{
                fontWeight: 700,
                fontSize: 12.5,
                letterSpacing: '.65px',
                textTransform: 'uppercase',
                color: '#9e5c2c',
                marginBottom: 10,
                display: 'flex',
                alignItems: 'center',
                gap: 6
              }}
            >
              <CreditCardIcon style={{ fontSize: 16, color: '#9e5c2c' }} /> POS
            </Typography>
            <TextField
              select
              fullWidth
              variant="outlined"
              size="small"
              value={selectedPos}
              onChange={(e) => setSelectedPos(e.target.value)}
              disabled={posMatchingLoading || posMatchingList.length === 0}
              style={{ backgroundColor: '#fff', borderRadius: 8 }}
              helperText={
                posMatchingLoading
                  ? 'Loading POS devices...'
                  : posMatchingList.length === 0
                  ? 'No POS devices available'
                  : 'Please select a POS device to proceed'
              }
            >
              {posMatchingList.map((pos) => (
                <MenuItem key={pos.Id} value={pos.Id}>
                  {pos.PosMachineIdentifier} ({pos.PosMachineTypeName})
                </MenuItem>
              ))}
            </TextField>
          </Box>
        )}

        {/* RepairPreSale trigger button */}
        {!readonly && (
          <Box mt={2}>
            <Button
              variant="contained"
              color="primary"
              startIcon={<CreditCardIcon />}
              onClick={handleRepairPreSale}
              disabled={repairLoading || totalWithVat <= 0 || !selectedPos}
            >
              {repairLoading
                ? <CircularProgress size={20} color="inherit" />
                : `${t('Payment')} - ${fmt(totalWithVat)} ${cur}`}
            </Button>
          </Box>
        )}

        {/* Loading state */}
        {repairLoading && (
          <Box display="flex" alignItems="center" justifyContent="center" mt={3} minHeight={120}>
            <CircularProgress size={48} />
            <Typography variant="body2" style={{ marginLeft: 12 }}>Processing...</Typography>
          </Box>
        )}

        {/* Error display */}
        {(localError || repairError) && (
          <Box mt={2}>
            <Typography variant="body2" color="error" style={{ fontWeight: 600 }}>
              {localError || (typeof repairError === 'string' ? repairError : JSON.stringify(repairError))}
            </Typography>
          </Box>
        )}

        {/* Payment reference info */}
        {repairData && !showIframe && (
          <Box mt={2}>
            <Typography variant="body2" color="textSecondary">
              Payment Reference: <strong>{repairData.PaymentReference || '-'}</strong>
            </Typography>
            {repairData.HasApplicationSingleProvider && (
              <Typography variant="body2" color="primary" style={{ marginTop: 4 }}>
                Payment link has been delivered to the customer.
              </Typography>
            )}
          </Box>
        )}

        {/* Payment iframe */}
        {showIframe && iframeUrl && (
          <Box mt={3}>
            <Typography variant="h6" gutterBottom align="center">
              Complete Your Payment
            </Typography>
            <Box display="flex" justifyContent="center" mb={2}>
              <Button variant="outlined" color="secondary" onClick={handleCloseIframe} size="small">
                Close Payment
              </Button>
            </Box>
            <Box
              display="flex"
              flexDirection="column"
              alignItems="center"
              style={{ minHeight: '400px' }}
            >
              <iframe
                src={iframeUrl}
                style={{
                  width: '100%',
                  height: '500px',
                  border: '1px solid #ccc',
                  borderRadius: '4px',
                  boxShadow: '0 4px 16px -6px rgba(0,0,0,.18)'
                }}
                title="Payment"
              />
            </Box>
          </Box>
        )}
      </CardContent>
    </Card>
  );
};

export default Payment;
